import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from "mobx-react";
import { 
  StatsPreview, 
  AddPlayerToTeamModal, 
  RemovePlayerFromTeamModal, 
  InviteUserModal, 
  DarkScreenMask,
  RefreshTeamCode
} from "@myplay/ui";

import { TeamCoaches, AthletesTable, TeamRecentVideos, HighlightPlayerModal } from '..';
import I18N from '../../I18N/index';
import { PLAYER, COACH, COACHES_ROUTE, MYPLAY_APP_ROUTE } from '../../utils/constants';
import { toTitleCase } from '../../utils/stringManipulations';
import history from '../../utils/history';
import "./SingleTeam.scss";

const SingleTeam = ({ team, TeamsStore, CoachesStore, ClubsStore }) => {
  const { attachUsersToTeam, getNotRelatedPlayers, removePlayerFromTeam, teams, refreshTeamCode } = TeamsStore;
  const { sendInvitations } = ClubsStore;
  const { members: { coaches, players, operators }, stats, videos, code, site, highlights } = team;
  const [shouldRenderAddPlayerModal, setShouldRenderAddPlayerModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);  
  const [modalUsers, setModalUsers] = useState([]);
  const [shouldRenderInviteUsersModal, setInviteUsersModal] = useState(false);
  const [isModalAccountTypePlayer, setModalAccountType] = useState(null);
  const [selectedHighlight, setSelectedHighlight] = useState(null);
  const notRelatedPlayers = getNotRelatedPlayers();
  const notRelatedCoaches = CoachesStore.getNotRelatedCoaches();

  const handleAddUsersToTeam = async usersIds => {
    await attachUsersToTeam(usersIds);
    setShouldRenderAddPlayerModal(false);
  };

  const handleShowAddUsersModal = userType => {
    setShouldRenderAddPlayerModal(true);
    setModalUsers(userType === PLAYER ? notRelatedPlayers : notRelatedCoaches);
    setModalAccountType(userType === PLAYER);
  };

  const handleDeleteUser = athlete => {
    setUserToDelete(athlete);
  };

  const handleDeleteUserApprove = async ()=> {
    await removePlayerFromTeam(userToDelete, team);
    setUserToDelete(null);
  };

  const handleCoachClick = coach => {
    history.push(`${COACHES_ROUTE}/?coachId=${coach._id}`);
  };

  const handleVideoSelect = video => {
    const { _id } = video;
    const route = `${MYPLAY_APP_ROUTE}/game/${_id}`;
    window.open(route, '_blank');
  };

  const handleSendInvitations = async users => {
    await sendInvitations(users, isModalAccountTypePlayer ? PLAYER : COACH);
    setInviteUsersModal(false);
  };

  const handleRefreshTeamCode = ()=> {
    refreshTeamCode(team._id);
  }

  const handleHighlightSelect = highlight => {
    setSelectedHighlight(highlight);
  };

  const handleCloseHighlightModal = ()=> {
    setSelectedHighlight(null);
  }

  return (
    <div className="single-team" data-testid="single-team">
      <div className="single-team__coaches">
        <p className="single-team__coaches__title">{toTitleCase(I18N.t("TEAM_COACHES"))}</p>
        <TeamCoaches
          coaches={[...operators, ...coaches]}
          onAssignCoach={() => handleShowAddUsersModal(COACH)}
          onCoachClick={handleCoachClick}
        />
      </div>
      <div className="single-team__players">
        <p className="single-team__players__title">{toTitleCase(I18N.t("TEAM_ROSTER"))}</p>
        <AthletesTable
          athletes={players}
          onAddPlayer={() => handleShowAddUsersModal(PLAYER)}
          onDeletePlayer={handleDeleteUser}
          currentTeamId={team._id}
        />
      </div>
      <div className="single-team__recent-videos">
        <div className="single-team__recent-videos__videos-container">
          {videos && videos.length > 0 && <TeamRecentVideos onClick={handleVideoSelect}/>}
          {highlights && highlights.length > 0 && <TeamRecentVideos isGames={false} onClick={handleHighlightSelect}/>} 
        </div>
      </div>
      <div className="single-team__stats-preview">
        <p className="single-team__stats-preview__title">{toTitleCase(I18N.t("RECENT_STATS"))}</p>
        {stats && Object.keys(stats).length > 0 &&
          <StatsPreview
            donuts={stats.donuts}
            bars={stats.bars}
            isStats={true}
            isSeasonAvg
          />
        }
      </div>
      <div className="single-team__court-and-team-code">
        <div className="single-team__court-and-team-code__court-name">
          <span>{`${toTitleCase(I18N.t("COURT"))}:`}</span>
          <span>{site ? site.name : ""}</span>
        </div>
        <RefreshTeamCode teamCode={code} onRefresh={handleRefreshTeamCode}/>
      </div>
      {shouldRenderAddPlayerModal &&
        <AddPlayerToTeamModal
          isVisible={shouldRenderAddPlayerModal}
          onClose={() => setShouldRenderAddPlayerModal(false)}
          users={modalUsers}
          onAddUsers={handleAddUsersToTeam}
          onInvite={()=> setInviteUsersModal(true)}
          accountType={isModalAccountTypePlayer ? PLAYER : COACH}
        />
      }
      {userToDelete &&
        <RemovePlayerFromTeamModal 
          name={userToDelete.fullName}
          teams={[team]} 
          isVisible={userToDelete}
          onClose={()=> setUserToDelete(null)} 
          onApprove={handleDeleteUserApprove}
        />
      }
      <DarkScreenMask isVisible={shouldRenderInviteUsersModal}>
        <InviteUserModal
          onClose={() => setInviteUsersModal(false)}
          title={isModalAccountTypePlayer ? toTitleCase(I18N.t("INVITE_NEW_ATHLETE")) : toTitleCase(I18N.t("INVITE_NEW_COACH"))}
          onSend={handleSendInvitations}
          teams={teams} 
        />
      </DarkScreenMask>
      <DarkScreenMask isVisible={selectedHighlight}>
        {selectedHighlight &&
          <HighlightPlayerModal 
            highlight={selectedHighlight} 
            onClose={handleCloseHighlightModal}
          />
        }
      </DarkScreenMask>
    </div>
  );
};

SingleTeam.propTypes = {
  ClubsStore: PropTypes.object, 
  CoachesStore: PropTypes.object,
  team: PropTypes.object, 
  TeamsStore: PropTypes.object
};

SingleTeam.defaultProps = {
  ClubsStore: {}, 
  CoachesStore: {},
  team: {}, 
  TeamsStore: {} 
};

export default inject("TeamsStore", "CoachesStore", "ClubsStore")(observer(SingleTeam));  